import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { ListingHoldReason, RecallCheckResult } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { Alert, Badge, Field, Input, Select, Textarea } from '../../components/ui';
import {
  useAdminCancelBookingForUnlistedCarMutation,
  useAdminEndTripEarlyForRecallMutation,
  useAdminRelistCarMutation,
  useCreatePresignedUrlForRecallEvidenceMutation,
  useRecordRecallCheckMutation,
  useUnlistedCarTripsQuery,
} from '../../services/listingHoldService/__generated__/queries.generated';
import {
  NHTSA_VIN_LOOKUP_URL,
  evidenceFileProblem,
  graphqlErrorMessage,
  localDayKey,
  openTripsOf,
  relistFormProblem,
  unlistedTripAction,
} from '../../utils/listingHold';

const REFETCH = [
  'UnlistedCars',
  'CarsPendingRecallCheck',
  'CarsWithoutRecallMatch',
  'RecallJobStatus',
];

export interface DialogCar {
  id: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  vin?: string | null;
  name?: string | null;
}

export interface DialogCampaign {
  campaignNumber: string;
  component?: string | null;
  summary?: string | null;
  remedyAvailable?: boolean | null;
  parkIt?: boolean;
  parkOutSide?: boolean;
  nhtsaUrl: string;
}

export interface DialogHold {
  reason: ListingHoldReason;
  pendingManualCheck: boolean;
  campaigns: DialogCampaign[];
}

export const carLabel = (car: DialogCar) =>
  `${car.year ?? ''} ${car.make ?? ''} ${car.model ?? ''}`.trim() || car.name || 'Car';

const Title = styled.h2`
  ${tw`text-lg font-semibold mb-1`}
  color: var(--color-text-primary);
`;
const Muted = styled.p`
  ${tw`text-sm mb-3`}
  color: var(--color-text-secondary);
`;
const Row = styled.div`
  ${tw`flex flex-wrap items-center gap-2 mb-3`}
`;
const CampaignRow = styled.label`
  ${tw`flex gap-2 items-start p-2 rounded-md mb-2 cursor-pointer`}
  border: 1px solid var(--color-border);
`;
const CampaignText = styled.div`
  ${tw`text-sm min-w-0`}
`;
const LinkText = styled.a`
  ${tw`text-sm underline`}
  color: var(--color-text-primary);
`;
const FileList = styled.ul`
  ${tw`text-sm list-none p-0 m-0 mb-2`}
  color: var(--color-text-secondary);
`;
const HiddenInput = styled.input`
  ${tw`hidden`}
`;
const TripRow = styled.div`
  ${tw`flex flex-wrap items-center justify-between gap-2 p-2 rounded-md mb-2 text-sm`}
  border: 1px solid var(--color-border);
`;

/** Copy VIN and open the NHTSA VIN lookup: the admin checks the VIN there by hand. */
export function VinTools({ vin }: { vin?: string | null }) {
  return (
    <Row>
      <Button
        theme="secondary"
        size="sm"
        type="button"
        text={vin ? `Copy VIN ${vin}` : 'No VIN on record'}
        isDisabled={!vin}
        dataTestId="copy-vin"
        onClick={() => {
          if (!vin) return;
          void navigator.clipboard?.writeText(vin).then(
            () => toast.success('VIN copied'),
            () => toast.error('Could not copy the VIN'),
          );
        }}
      />
      <LinkText href={NHTSA_VIN_LOOKUP_URL} target="_blank" rel="noopener noreferrer">
        Open NHTSA VIN lookup
      </LinkText>
    </Row>
  );
}

/** Uploads evidence files for one car; returns their keys. */
function useEvidenceUpload(carId: string) {
  const [presign] = useCreatePresignedUrlForRecallEvidenceMutation();
  const [files, setFiles] = useState<Array<{ name: string; key: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const add = async (list: FileList | null) => {
    if (!list?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(list)) {
        const problem = evidenceFileProblem(file);
        if (problem) {
          toast.error(`${file.name}: ${problem}`);
          continue;
        }
        const { data } = await presign({
          variables: { carId, fileType: file.type, contentLength: file.size },
        });
        const target = data?.createPresignedUrlForRecallEvidence;
        if (!target) throw new Error('No upload URL');
        const response = await fetch(target.url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!response.ok) throw new Error(`Upload failed (${response.status})`);
        setFiles(current => [...current, { name: file.name, key: target.key }]);
      }
    } catch (error) {
      toast.error(graphqlErrorMessage(error, (error as Error)?.message || 'Upload failed'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const picker = (
    <>
      <HiddenInput
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,application/pdf"
        data-testid="evidence-input"
        onChange={event => void add(event.target.files)}
      />
      {files.length > 0 && (
        <FileList data-testid="evidence-files">
          {files.map(file => (
            <li key={file.key}>{file.name}</li>
          ))}
        </FileList>
      )}
      <Button
        theme="secondary"
        size="sm"
        type="button"
        text={uploading ? 'Uploading…' : 'Upload screenshot or repair order'}
        isDisabled={uploading}
        onClick={() => inputRef.current?.click()}
      />
    </>
  );
  return { keys: files.map(file => file.key), uploading, picker };
}

/** FR-022: relist with evidence and every campaign confirmed. */
export function RelistDialog({
  car,
  hold,
  onClose,
}: {
  car: DialogCar;
  hold: DialogHold;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const evidence = useEvidenceUpload(car.id);
  const [relist, { loading }] = useAdminRelistCarMutation({
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const isRecall = hold.reason === ListingHoldReason.OpenRecall;
  const problem = relistFormProblem({
    hold,
    checkedCampaigns: checked,
    evidenceCount: evidence.keys.length,
    uploading: evidence.uploading,
  });

  const submit = async () => {
    setServerError(null);
    try {
      await relist({
        variables: {
          input: {
            carId: car.id,
            evidenceKeys: evidence.keys,
            clearedCampaignNumbers: checked,
            note: note.trim() || null,
          },
        },
      });
      toast.success('Car relisted. The host was emailed.');
      onClose();
    } catch (error) {
      setServerError(graphqlErrorMessage(error, 'Could not relist the car.'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Relist car">
      <div data-testid="relist-dialog">
        <Title>Relist {carLabel(car)}</Title>
        {isRecall ? (
          <Muted>
            Check the VIN on NHTSA&apos;s lookup. Relist only when every recall below is repaired or
            does not apply to this VIN, and upload the screenshot or the dealer&apos;s repair order.
            The car will not be unlisted again for these recalls.
          </Muted>
        ) : (
          <Muted>The car goes back into search. Evidence is optional.</Muted>
        )}
        {isRecall && <VinTools vin={car.vin} />}
        {hold.campaigns.map(campaign => (
          <CampaignRow key={campaign.campaignNumber}>
            <input
              type="checkbox"
              data-testid={`relist-campaign-${campaign.campaignNumber}`}
              checked={checked.includes(campaign.campaignNumber)}
              onChange={event =>
                setChecked(current =>
                  event.target.checked
                    ? [...current, campaign.campaignNumber]
                    : current.filter(number => number !== campaign.campaignNumber),
                )
              }
            />
            <CampaignText>
              <Row>
                <LinkText href={campaign.nhtsaUrl} target="_blank" rel="noopener noreferrer">
                  {campaign.campaignNumber}
                </LinkText>
                {campaign.component && <span>{campaign.component}</span>}
                {campaign.remedyAvailable === false && (
                  <Badge tone="warning" size="sm">
                    No fix yet
                  </Badge>
                )}
                {(campaign.parkIt || campaign.parkOutSide) && (
                  <Badge tone="error" size="sm">
                    {campaign.parkIt ? 'Do not drive' : 'Park outside'}
                  </Badge>
                )}
              </Row>
              {campaign.summary}
            </CampaignText>
          </CampaignRow>
        ))}
        {evidence.picker}
        <Field label="Note (optional)" htmlFor="relist-note">
          <Textarea
            id="relist-note"
            rows={2}
            maxLength={2000}
            value={note}
            onChange={event => setNote(event.target.value)}
          />
        </Field>
        {serverError && (
          <Alert tone="error" data-testid="relist-error">
            {serverError}
          </Alert>
        )}
        {problem && isRecall && <Muted data-testid="relist-problem">{problem}</Muted>}
        <Button
          theme="filled"
          type="button"
          text="Relist car"
          dataTestId="relist-submit"
          isDisabled={Boolean(problem)}
          isLoading={loading}
          onClick={() => void submit()}
          marginTop={12}
        />
      </div>
    </Modal>
  );
}

/** FR-026: record a manual recall check. */
export function RecordCheckDialog({ car, onClose }: { car: DialogCar; onClose: () => void }) {
  const [checkedOn, setCheckedOn] = useState(localDayKey());
  const [result, setResult] = useState<RecallCheckResult>(RecallCheckResult.Clear);
  const [note, setNote] = useState('');
  const evidence = useEvidenceUpload(car.id);
  const [record, { loading }] = useRecordRecallCheckMutation({
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });

  const submit = async () => {
    try {
      await record({
        variables: {
          input: {
            carId: car.id,
            checkedOn,
            result,
            attachmentKeys: evidence.keys,
            note: note.trim() || null,
          },
        },
      });
      toast.success(
        result === RecallCheckResult.Clear
          ? 'Recall check recorded'
          : 'Recall recorded. The car is unlisted and its future trips are cancelled.',
      );
      onClose();
    } catch (error) {
      toast.error(graphqlErrorMessage(error, 'Could not record the check'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Record recall check">
      <div data-testid="record-check-dialog">
        <Title>Recall check: {carLabel(car)}</Title>
        <Muted>Look the VIN up on NHTSA and record what it shows.</Muted>
        <VinTools vin={car.vin} />
        <Field label="Checked on" htmlFor="recall-checked-on" required>
          <Input
            id="recall-checked-on"
            type="date"
            max={localDayKey()}
            value={checkedOn}
            onChange={event => setCheckedOn(event.target.value)}
          />
        </Field>
        <Field label="Result" htmlFor="recall-result" required>
          <Select
            id="recall-result"
            data-testid="recall-result"
            value={result}
            onChange={event => setResult(event.target.value as RecallCheckResult)}
          >
            <option value={RecallCheckResult.Clear}>No open recalls</option>
            <option value={RecallCheckResult.OpenRecall}>Open recall (unlists the car)</option>
          </Select>
        </Field>
        {evidence.picker}
        <Field label="Note (optional)" htmlFor="recall-note">
          <Textarea
            id="recall-note"
            rows={2}
            maxLength={2000}
            value={note}
            onChange={event => setNote(event.target.value)}
          />
        </Field>
        <Button
          theme="filled"
          type="button"
          text="Record check"
          dataTestId="record-check-submit"
          isDisabled={!checkedOn || evidence.uploading}
          isLoading={loading}
          onClick={() => void submit()}
          marginTop={12}
        />
      </div>
    </Modal>
  );
}

/** US-3, US-8: an unlisted car's open trips with the admin actions. */
export function TripsDialog({
  car,
  holdReason,
  onClose,
}: {
  car: DialogCar;
  holdReason: ListingHoldReason;
  onClose: () => void;
}) {
  const { data, loading, refetch } = useUnlistedCarTripsQuery({
    variables: { carId: car.id },
    fetchPolicy: 'network-only',
  });
  const refetchAll = { refetchQueries: REFETCH };
  const [cancelTrip] = useAdminCancelBookingForUnlistedCarMutation(refetchAll);
  const [endEarly] = useAdminEndTripEarlyForRecallMutation(refetchAll);
  const [busy, setBusy] = useState<string | null>(null);
  const now = Date.now();
  const trips = openTripsOf(data?.carBookings ?? [], now);

  const run = async (id: string, action: 'cancel' | 'endEarly') => {
    const question =
      action === 'cancel'
        ? 'Cancel this trip with a full refund? The guest and host are emailed.'
        : 'End this trip now and refund the unused time? The guest and host are emailed.';
    if (!window.confirm(question)) return;
    setBusy(id);
    try {
      if (action === 'cancel') {
        await cancelTrip({ variables: { bookingId: id } });
        toast.success('Trip cancelled and refunded');
      } else {
        await endEarly({ variables: { bookingId: id, endAt: Date.now() } });
        toast.success('Trip ended; unused time refunded');
      }
      await refetch();
    } catch (error) {
      toast.error(graphqlErrorMessage(error, 'Could not change the trip'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Trips on this car">
      <div data-testid="trips-dialog">
        <Title>Trips on {carLabel(car)}</Title>
        <Muted>Open trips on this site. Swap the car from the trip page, or cancel here.</Muted>
        {loading && <Muted>Loading…</Muted>}
        {!loading && trips.length === 0 && <Muted>No open trips.</Muted>}
        {trips.map(trip => {
          const action = unlistedTripAction(trip, holdReason, now);
          const guest = [trip.guest?.firstName, trip.guest?.lastName].filter(Boolean).join(' ');
          return (
            <TripRow key={trip.id} data-testid={`unlisted-trip-${trip.id}`}>
              <span>
                {new Date(trip.startAt).toLocaleString()} → {new Date(trip.endAt).toLocaleString()}
                {guest ? ` · ${guest}` : ''}
              </span>
              {action && (
                <Button
                  theme={action === 'cancel' ? 'danger' : 'secondary'}
                  size="sm"
                  type="button"
                  text={action === 'cancel' ? 'Cancel with full refund' : 'End trip now'}
                  isLoading={busy === trip.id}
                  isDisabled={Boolean(busy)}
                  onClick={() => void run(trip.id, action)}
                />
              )}
            </TripRow>
          );
        })}
      </div>
    </Modal>
  );
}
